import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiGet, apiPost, apiUpload, apiDownload } from '../../../lib/api';
import { toast } from 'sonner';
import { ArrowLeft, Plus, X, Users, CheckCircle2, Upload, FileText, Trash2, Search } from 'lucide-react';
import { formatDate } from '../../../lib/format';
import { useAuth } from '../../../contexts/AuthContext';

const SESSION_STATUS = {
  scheduled: 'bg-teal-50 text-teal-700 border-teal-200',
  completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  cancelled: 'bg-slate-100 text-slate-400 border-slate-300',
};

export default function TrainingPlanDetail() {
  const { pid } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [plan, setPlan] = useState(null);
  const [records, setRecords] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [showSession, setShowSession] = useState(false);
  const [completeFor, setCompleteFor] = useState(null);

  const role = (user?.role || '').toLowerCase();
  const dept = (user?.department || '').toUpperCase();
  const canWrite = ['super_admin', 'admin', 'hr', 'md'].includes(role) || dept === 'HR' || dept === 'MD';

  const load = useCallback(() => {
    apiGet(`/hr/training/plans/${pid}`).then(setPlan).catch(() => toast.error('Plan not found'));
    apiGet('/hr/training/records', { plan_id: pid }).then((r) => setRecords(r.items || [])).catch(() => {});
  }, [pid]);
  useEffect(() => { load(); apiGet('/hr/employees').then((l) => setEmployees((l || []).map((e) => ({ id: e.id, name: e.name, emp_id: e.emp_id, department: e.department })))).catch(() => {}); }, [load]);

  if (!plan) return <div className="p-8 text-slate-500">Loading…</div>;

  return (
    <div className="p-6 md:p-8 space-y-5 max-w-5xl" data-testid="training-plan-detail">
      <button onClick={() => nav('/app/hr/training')} className="text-xs text-slate-500 flex items-center gap-1 hover:text-[#1F7A6E]" data-testid="back-to-training"><ArrowLeft size={14} /> All Plans</button>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-extrabold tracking-tight">{plan.plan_name}</h1>
          <p className="text-sm text-slate-600 mt-1">{plan.category || 'Uncategorised'}{plan.default_expiry_months ? ` · Validity ${plan.default_expiry_months} months` : ''}</p>
          {plan.description && <p className="text-xs text-slate-500 mt-2 max-w-2xl">{plan.description}</p>}
        </div>
        {canWrite && <button onClick={() => setShowSession(true)} data-testid="add-session-btn" className="px-4 py-2 bg-[#1F7A6E] text-white rounded-sm text-xs font-display font-bold uppercase tracking-wider hover:bg-[#2e3a5c] flex items-center gap-2"><Plus size={14} /> Add Session</button>}
      </div>

      {/* Sessions */}
      <div className="bg-white border border-slate-200 rounded-sm">
        <div className="px-4 py-3 border-b"><h3 className="text-xs font-display font-bold uppercase tracking-wider text-slate-500">Sessions</h3></div>
        {(plan.sessions || []).length === 0 ? <div className="p-4 text-xs text-slate-400">No sessions yet.</div> : (
          <div className="divide-y divide-slate-100">
            {plan.sessions.map((s) => (
              <div key={s.session_id} className="p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`session-${s.session_id}`}>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-display font-bold text-sm">{s.title}</span>
                    <span className={`text-[9px] font-bold uppercase px-2 py-0.5 rounded-sm border ${SESSION_STATUS[s.status]}`}>{s.status}</span>
                    <span className={`text-[9px] font-bold uppercase px-2 py-0.5 rounded-sm border ${s.trainer_type === 'external' ? 'bg-violet-50 text-violet-700 border-violet-200' : 'bg-slate-100 text-slate-600 border-slate-300'}`}>{s.trainer_type}</span>
                  </div>
                  <div className="text-[11px] text-slate-500 mt-1">
                    {s.scheduled_at ? formatDate(s.scheduled_at) : 'TBD'}
                    {s.trainer_name ? ` · ${s.trainer_name}` : ''}{s.vendor ? ` (${s.vendor})` : ''}
                    {s.location ? ` · ${s.location}` : ''}
                    {s.expiry_months ? ` · valid ${s.expiry_months}mo` : ''}
                    {` · ${(s.invited || []).length} invited`}
                    {s.status === 'completed' && s.attended_count != null ? ` · ${s.attended_count} attended` : ''}
                  </div>
                </div>
                {canWrite && s.status === 'scheduled' && (
                  <button onClick={() => setCompleteFor(s)} data-testid={`complete-session-${s.session_id}`} className="px-3 py-1.5 border border-emerald-300 text-emerald-700 rounded-sm text-[11px] font-display font-bold uppercase tracking-wider hover:bg-emerald-50 flex items-center gap-1.5"><CheckCircle2 size={13} /> Mark Complete</button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Certifications */}
      <div className="bg-white border border-slate-200 rounded-sm">
        <div className="px-4 py-3 border-b"><h3 className="text-xs font-display font-bold uppercase tracking-wider text-slate-500">Certifications ({records.length})</h3></div>
        {records.length === 0 ? <div className="p-4 text-xs text-slate-400">No certifications recorded yet.</div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-slate-500"><tr>
                {['Employee', 'Session', 'Score', 'Completed', 'Expiry', 'Status', 'Certificate'].map((h) => <th key={h} className="text-left px-3 py-2 font-display font-bold uppercase tracking-wider text-[10px]">{h}</th>)}
              </tr></thead>
              <tbody className="divide-y divide-slate-100">
                {records.map((r) => <RecordRow key={r.id} r={r} canWrite={canWrite} onChange={load} />)}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showSession && <SessionForm pid={pid} employees={employees} defaultExpiry={plan.default_expiry_months} onClose={() => setShowSession(false)} onSaved={() => { setShowSession(false); load(); }} />}
      {completeFor && <CompleteDialog pid={pid} session={completeFor} employees={employees} onClose={() => setCompleteFor(null)} onDone={() => { setCompleteFor(null); load(); }} />}
      <style>{`.inp{width:100%;border:1px solid #e2e8f0;border-radius:2px;padding:8px 10px;font-size:13px;outline:none}.inp:focus{border-color:#1F7A6E}.lbl{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.05em;color:#64748b;display:block;margin-bottom:4px}`}</style>
    </div>
  );
}

function RecordRow({ r, canWrite, onChange }) {
  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    try { await apiUpload(`/hr/training/records/${r.id}/certificate`, fd); toast.success('Certificate uploaded'); onChange(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Upload failed'); }
  };
  const sc = { valid: 'bg-emerald-50 text-emerald-700 border-emerald-200', expiring: 'bg-amber-50 text-amber-700 border-amber-200', expired: 'bg-red-50 text-red-600 border-red-200', no_expiry: 'bg-slate-100 text-slate-500 border-slate-300' };
  return (
    <tr data-testid={`record-${r.id}`}>
      <td className="px-3 py-2"><div className="font-display font-bold">{r.employee_name}</div><div className="text-slate-400 text-[10px]">{r.emp_id || ''}</div></td>
      <td className="px-3 py-2 text-slate-600">{r.session_title}</td>
      <td className="px-3 py-2">{r.score != null ? `${r.score}` : '—'}</td>
      <td className="px-3 py-2 text-slate-500">{r.completed_at ? formatDate(r.completed_at) : '—'}</td>
      <td className="px-3 py-2 text-slate-500">{r.expiry_date || '—'}</td>
      <td className="px-3 py-2"><span className={`text-[9px] font-bold uppercase px-2 py-0.5 rounded-sm border ${sc[r.status] || sc.no_expiry}`}>{r.status === 'no_expiry' ? 'No expiry' : r.status}</span></td>
      <td className="px-3 py-2">
        {r.certificate_url ? (
          <button onClick={() => apiDownload(`/hr/training/records/${r.id}/certificate`, `cert_${r.employee_name}`)} data-testid={`cert-dl-${r.id}`} className="text-[#1F7A6E] flex items-center gap-1 font-bold"><FileText size={12} /> View</button>
        ) : canWrite ? (
          <label className="text-slate-500 flex items-center gap-1 cursor-pointer hover:text-[#1F7A6E]" data-testid={`cert-up-${r.id}`}><Upload size={12} /> Upload<input type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={(e) => upload(e.target.files[0])} /></label>
        ) : <span className="text-slate-300">—</span>}
      </td>
    </tr>
  );
}

function EmployeeMultiSelect({ employees, selected, onToggle }) {
  const [q, setQ] = useState('');
  const filtered = employees.filter((e) => !q || (e.name || '').toLowerCase().includes(q.toLowerCase()) || (e.emp_id || '').toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="border border-slate-200 rounded-sm">
      <div className="flex items-center gap-2 px-2 py-1.5 border-b"><Search size={13} className="text-slate-400" /><input className="flex-1 text-xs outline-none" placeholder="Search employees…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="emp-search" /></div>
      <div className="max-h-44 overflow-y-auto">
        {filtered.length === 0 ? <div className="text-[11px] text-slate-400 p-3">No employees.</div> : filtered.map((e) => {
          const on = selected.some((s) => s.employee_id === e.id);
          return (
            <button type="button" key={e.id} onClick={() => onToggle(e)} data-testid={`emp-opt-${e.id}`} className={`w-full flex items-center justify-between px-3 py-1.5 text-xs text-left hover:bg-slate-50 ${on ? 'bg-[#1F7A6E]/5' : ''}`}>
              <span>{e.name} <span className="text-slate-400">· {e.department || ''}</span></span>
              {on && <CheckCircle2 size={14} className="text-[#1F7A6E]" />}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function SessionForm({ pid, employees, defaultExpiry, onClose, onSaved }) {
  const [f, setF] = useState({ title: '', trainer_type: 'internal', trainer_name: '', vendor: '', scheduled_at: '', location: '', expiry_months: defaultExpiry || '' });
  const [invited, setInvited] = useState([]);
  const [busy, setBusy] = useState(false);
  const toggle = (e) => setInvited((prev) => prev.some((s) => s.employee_id === e.id) ? prev.filter((s) => s.employee_id !== e.id) : [...prev, { employee_id: e.id, name: e.name, department: e.department, emp_id: e.emp_id }]);
  const submit = async (ev) => {
    ev.preventDefault();
    if (!f.title.trim()) { toast.error('Session title is required'); return; }
    setBusy(true);
    try {
      await apiPost(`/hr/training/plans/${pid}/sessions`, { ...f, expiry_months: f.expiry_months ? Number(f.expiry_months) : null, invited });
      toast.success('Session added'); onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40" onClick={onClose} />
      <div className="fixed inset-y-0 right-0 w-full max-w-md bg-white shadow-2xl z-50 flex flex-col" data-testid="session-form-drawer">
        <header className="px-5 py-4 border-b flex items-center justify-between bg-[#1F7A6E] text-white"><h2 className="font-display font-bold tracking-wide uppercase text-sm">Add Session</h2><button onClick={onClose}><X size={20} /></button></header>
        <form onSubmit={submit} className="flex-1 overflow-y-auto p-5 space-y-3">
          <label className="block"><span className="lbl">Session Title *</span><input data-testid="session-title-input" className="inp" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} /></label>
          <label className="block"><span className="lbl">Trainer Type</span>
            <select data-testid="trainer-type-select" className="inp" value={f.trainer_type} onChange={(e) => setF({ ...f, trainer_type: e.target.value })}><option value="internal">Internal</option><option value="external">External (Vendor)</option></select></label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block"><span className="lbl">Trainer Name</span><input className="inp" value={f.trainer_name} onChange={(e) => setF({ ...f, trainer_name: e.target.value })} /></label>
            {f.trainer_type === 'external' && <label className="block"><span className="lbl">Vendor</span><input data-testid="vendor-input" className="inp" value={f.vendor} onChange={(e) => setF({ ...f, vendor: e.target.value })} /></label>}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block"><span className="lbl">Scheduled Date</span><input type="date" data-testid="session-date-input" className="inp" value={f.scheduled_at} onChange={(e) => setF({ ...f, scheduled_at: e.target.value })} /></label>
            <label className="block"><span className="lbl">Validity (months)</span><input type="number" className="inp" value={f.expiry_months} onChange={(e) => setF({ ...f, expiry_months: e.target.value })} /></label>
          </div>
          <label className="block"><span className="lbl">Location</span><input className="inp" value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} /></label>
          <div><span className="lbl flex items-center gap-1"><Users size={12} /> Invite Employees ({invited.length})</span>
            <EmployeeMultiSelect employees={employees} selected={invited} onToggle={toggle} /></div>
          <button type="submit" disabled={busy} data-testid="session-submit-btn" className="w-full py-2.5 bg-[#1F7A6E] text-white rounded-sm text-xs font-display font-bold uppercase tracking-wider hover:bg-[#2e3a5c] disabled:opacity-50">{busy ? 'Saving…' : 'Add Session'}</button>
        </form>
        <style>{`.inp{width:100%;border:1px solid #e2e8f0;border-radius:2px;padding:8px 10px;font-size:13px;outline:none}.inp:focus{border-color:#1F7A6E}.lbl{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.05em;color:#64748b;display:block;margin-bottom:4px}`}</style>
      </div>
    </>
  );
}

function CompleteDialog({ pid, session, employees, onClose, onDone }) {
  // Pre-fill attendees from invited; allow adding more + ticking attendance + score.
  const initial = (session.invited || []).map((i) => ({ employee_id: i.employee_id, name: i.name, department: i.department, emp_id: i.emp_id, attended: true, score: '' }));
  const [rows, setRows] = useState(initial);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);

  const setRow = (idx, patch) => setRows((p) => p.map((r, i) => i === idx ? { ...r, ...patch } : r));
  const addEmp = (e) => { if (rows.some((r) => r.employee_id === e.id)) { setAdding(false); return; } setRows((p) => [...p, { employee_id: e.id, name: e.name, department: e.department, emp_id: e.emp_id, attended: true, score: '' }]); setAdding(false); };

  const submit = async () => {
    setBusy(true);
    try {
      const attendees = rows.map((r) => ({ employee_id: r.employee_id, name: r.name, department: r.department, emp_id: r.emp_id, attended: r.attended, score: r.score === '' ? null : Number(r.score) }));
      const res = await apiPost(`/hr/training/plans/${pid}/sessions/${session.session_id}/complete`, { attendees, completed_at: date });
      toast.success(`Session completed · ${res.records_created} certified`); onDone();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="complete-dialog">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-sm w-full max-w-lg p-5 space-y-3 max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between"><h3 className="font-display font-bold text-sm">Complete — {session.title}</h3><button onClick={onClose}><X size={18} /></button></div>
        <label className="block"><span className="lbl">Completion Date</span><input type="date" data-testid="complete-date-input" className="inp w-auto" value={date} onChange={(e) => setDate(e.target.value)} /></label>
        <p className="text-[11px] text-slate-500">Tick who <b>actually attended</b>. Certifications are created only for attendees.</p>
        <div className="flex-1 overflow-y-auto border border-slate-100 rounded-sm divide-y divide-slate-100">
          {rows.length === 0 ? <div className="p-3 text-xs text-slate-400">No attendees. Add some below.</div> : rows.map((r, idx) => (
            <div key={r.employee_id} className="flex items-center gap-2 px-3 py-2" data-testid={`attendee-${r.employee_id}`}>
              <input type="checkbox" checked={r.attended} onChange={(e) => setRow(idx, { attended: e.target.checked })} data-testid={`attend-${r.employee_id}`} />
              <div className="flex-1 text-xs"><span className="font-display font-bold">{r.name}</span> <span className="text-slate-400">· {r.department || ''}</span></div>
              <input type="number" placeholder="Score" className="w-20 inp" value={r.score} onChange={(e) => setRow(idx, { score: e.target.value })} data-testid={`score-${r.employee_id}`} disabled={!r.attended} />
            </div>
          ))}
        </div>
        {adding ? (
          <EmployeeMultiSelect employees={employees} selected={rows.map((r) => ({ employee_id: r.employee_id }))} onToggle={addEmp} />
        ) : (
          <button type="button" onClick={() => setAdding(true)} data-testid="add-attendee-btn" className="text-xs text-[#1F7A6E] font-bold flex items-center gap-1"><Plus size={13} /> Add walk-in attendee</button>
        )}
        <button onClick={submit} disabled={busy} data-testid="complete-submit-btn" className="w-full py-2.5 bg-emerald-600 text-white rounded-sm text-xs font-display font-bold uppercase tracking-wider hover:bg-emerald-700 disabled:opacity-50">{busy ? 'Saving…' : 'Complete & Certify'}</button>
        <style>{`.inp{border:1px solid #e2e8f0;border-radius:2px;padding:6px 8px;font-size:13px;outline:none}.inp:focus{border-color:#1F7A6E}.lbl{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.05em;color:#64748b;display:block;margin-bottom:4px}`}</style>
      </div>
    </div>
  );
}
